import type { Theme, Direction, CommonColors, ThemeProviderProps } from '@mui/material/styles';
import type { ThemeCssVariables } from './types';
import type { PaletteColorKey, PaletteColorNoChannels } from './core/palette';

// ----------------------------------------------------------------------

export type GradientColor = {
  background: string;
  hover: string;
  /** Individual color stops for canvas/SVG use */
  colors: [string, string, string, string, string];
};

export type ThemeConfig = {
  direction: Direction;
  classesPrefix: string;
  cssVariables: ThemeCssVariables;
  defaultMode: ThemeProviderProps<Theme>['defaultMode'];
  modeStorageKey: ThemeProviderProps<Theme>['modeStorageKey'];
  fontFamily: Record<'primary' | 'secondary', string>;
  gradients: {
    primary: GradientColor;
    /** Per-tier accent for the plan card wash on /upgrade. */
    tiers: Record<'core' | 'pro' | 'ultra', string>;
  };
  palette: Record<PaletteColorKey, PaletteColorNoChannels> & {
    common: Pick<CommonColors, 'black' | 'white'>;
    grey: {
      [K in 50 | 100 | 200 | 300 | 400 | 500 | 600 | 700 | 800 | 900 | 950 as `${K}`]: string;
    };
  };
};

export const themeConfig: ThemeConfig = {
  /** **************************************
   * Base
   *************************************** */
  defaultMode: 'light',
  modeStorageKey: 'theme-mode',
  direction: 'ltr',
  classesPrefix: 'minimal',
  /** **************************************
   * Css variables
   *************************************** */
  cssVariables: {
    cssVarPrefix: '',
    colorSchemeSelector: 'data-color-scheme',
  },
  /** **************************************
   * Typography
   *************************************** */
  fontFamily: {
    primary: 'Inter',
    secondary: 'Inter',
  },
  /** **************************************
   * Gradients
   *************************************** */
  gradients: {
    primary: {
      background: 'linear-gradient(45deg, #F88159 0%, #DD6984 25%, #C86AC6 50%, #8966E1 75%, #6271F8 100%)',
      hover: 'linear-gradient(45deg, #F76E41 0%, #D85473 25%, #C157BF 50%, #7951DD 75%, #4A5BF7 100%)',
      colors: ['#F88159', '#DD6984', '#C86AC6', '#8966E1', '#6271F8'],
    },
    tiers: {
      core: '#6271F8',
      pro: '#8966E1',
      ultra: '#C86AC6',
    },
  },
  /** **************************************
   * Palette
   *************************************** */
  palette: {
    primary: {
      lighter: '#C8FAD6',
      light: '#5BE49B',
      main: '#00A76F',
      dark: '#007867',
      darker: '#004B50',
      contrastText: '#FFFFFF',
    },
    secondary: {
      lighter: '#EFD6FF',
      light: '#C684FF',
      main: '#A3A3A3',
      dark: '#5119B7',
      darker: '#27097A',
      contrastText: '#FFFFFF',
    },
    info: {
      lighter: '#CAFDF5',
      light: '#61F3F3',
      main: '#00B8D9',
      dark: '#006C9C',
      darker: '#003768',
      contrastText: '#FFFFFF',
    },
    success: {
      lighter: '#D3F5A0',
      light: '#A3E635',
      main: '#6BB700', // Ari status/success
      dark: '#4D8400',
      darker: '#2D5000',
      contrastText: '#FFFFFF',
    },
    warning: {
      lighter: '#FFEACC',
      light: '#FFC266',
      main: '#FFAA44', // Ari status/warning
      dark: '#CC7A00',
      darker: '#995200',
      contrastText: '#1C252E',
    },
    error: {
      lighter: '#FFDDDD',
      light: '#F28B8B',
      main: '#9E4042', // Ari status/error
      dark: '#773637',
      darker: '#7A0909',
      contrastText: '#FFFFFF',
    },
    grey: {
      // Tailwind neutral scale (aligned with Figma design system)
      50: '#FAFAFA',
      100: '#F5F5F5',
      200: '#E5E5E5',
      300: '#D4D4D4',
      400: '#A3A3A3',
      500: '#737373',
      600: '#525252',
      700: '#404040',
      800: '#262626',
      900: '#171717',
      950: '#0A0A0A',
    },
    common: {
      black: '#000000',
      white: '#FFFFFF',
    },
  },
};

// ----------------------------------------------------------------------
// Icon size constants for consistent sizing across components
// ----------------------------------------------------------------------

export const iconSizes = {
  xs: 12, // CircularProgress, small indicators
  sm: 14, // Caption-level icons
  md: 20, // Standard inline icons
  lg: 24, // Standard icon size
  xl: 40, // Icon container dimensions
  button: 36, // IconButton dimensions
} as const;

// ----------------------------------------------------------------------
// Border radius tokens for consistent rounding across components
// ----------------------------------------------------------------------

export const borderRadius = {
  xs: 4,
  sm: 8,
  md: 12,
  lg: 16,
  xl: 24,
  full: 9999,
} as const;

// ----------------------------------------------------------------------
// Z-index tokens for consistent layering across components
// ----------------------------------------------------------------------

export const zIndex = {
  base: 0,
  dropdown: 1000,
  sticky: 1100,
  appBar: 1200,
  drawer: 1300,
  modal: 1400,
  popover: 1500,
  tooltip: 1600,
} as const;

// ----------------------------------------------------------------------
// Ari Design System Color Reference (from Figma)
// Grey scale uses Tailwind neutral numbering (50-950)
// - Neutral scale → theme.palette.grey (Tailwind numbering)
// - Status colors → theme.palette.success/error/warning.main
// - Container → theme.palette.container.low/medium/high
// - Gradient → themeConfig.gradients.primary.colors
// - Text levels → theme.palette.text.primary (level1), grey[300] (level2), grey[500] (level3)
// ----------------------------------------------------------------------
